// Minimal keyboard InputSource so the scaffold is playable on desktop for
// testing. Written fresh for client3k (does NOT import the desktop client's
// keyboard.ts — packages/client stays untouched). Wave 2b-1's touch controls
// replace this via GameContext.setInputSource(); keep both attached is fine
// too since main.ts polls exactly one source.
//
// Bindings: WASD/arrows drive, Space shoots, Tab drops a mine, +/- adjusts
// gunsight range.

import { TankKey, type BuildCommand } from '@bolo2k/sim';
import type { InputSource } from './inputSource.js';

const HOLD_KEYS: Record<string, number> = {
  KeyW: TankKey.Faster,
  ArrowUp: TankKey.Faster,
  KeyS: TankKey.Slower,
  ArrowDown: TankKey.Slower,
  KeyA: TankKey.TurnLeft,
  ArrowLeft: TankKey.TurnLeft,
  KeyD: TankKey.TurnRight,
  ArrowRight: TankKey.TurnRight,
  Space: TankKey.Shoot,
  Tab: TankKey.DropMine,
  Equal: TankKey.MoreRange,
  Minus: TankKey.LessRange,
};

export class KeyboardFallbackInput implements InputSource {
  private held = new Set<string>();
  private detach: () => void;

  constructor(target: Window = window) {
    const onDown = (e: KeyboardEvent) => {
      if (isTextEditing(document.activeElement)) return;
      if (e.code in HOLD_KEYS) {
        this.held.add(e.code);
        // Tab would move focus, Space would scroll/click — both are game keys.
        if (e.code === 'Tab' || e.code === 'Space') e.preventDefault();
      }
    };
    const onUp = (e: KeyboardEvent) => {
      // Always honour key-up so a key held across a focus change can't stick.
      this.held.delete(e.code);
    };
    const onBlur = () => this.held.clear();
    target.addEventListener('keydown', onDown as EventListener);
    target.addEventListener('keyup', onUp as EventListener);
    target.addEventListener('blur', onBlur);
    this.detach = () => {
      target.removeEventListener('keydown', onDown as EventListener);
      target.removeEventListener('keyup', onUp as EventListener);
      target.removeEventListener('blur', onBlur);
    };
  }

  readKeys(): number {
    let bits = 0;
    for (const code of this.held) bits |= HOLD_KEYS[code] ?? 0;
    return bits;
  }

  consumeBuild(): BuildCommand | null {
    // Keyboard fallback issues no build commands — build targeting on this
    // scaffold comes from tapping the stage with an action armed (main.ts).
    return null;
  }

  dispose(): void {
    this.detach();
    this.held.clear();
  }
}

function isTextEditing(el: Element | null): boolean {
  if (!el) return false;
  if (el instanceof HTMLInputElement) return el.type === 'text' || el.type === 'search';
  if (el instanceof HTMLTextAreaElement) return true;
  return false;
}
